import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface ProcessedRow {
  messageId: string;
  notePath: string | null;
  fromAddr: string | null;
  subject: string | null;
  status: 'processing' | 'noted' | 'skipped' | 'error';
  reason: string | null;
  processedAt: number;
}

interface MailboxStatus {
  address: string;
  configured: boolean;
  hint?: string;
  watermark: number;
  noted: number;
  skipped: number;
  errors: number;
  processing: number;
}

interface EmailStatus {
  enabled: boolean;
  configured: boolean;
  backend: 'hostinger' | 'imap';
  provider: 'hostinger' | 'smtp';
  mailbox: string;
  mailboxes: MailboxStatus[];
  pollIntervalMs: number;
  sendConfirmation: boolean;
  recipients: string[];
  /** Trust mode (Microsoft 365): Exchange's trusted-senders list decides, and
   * the allow-list is not consulted. Null/absent = the allow-list decides. */
  trustHeader?: string | null;
  allowlist: string[];
  recent: ProcessedRow[];
}

type State = 'loading' | 'error' | EmailStatus;

/**
 * Settings → "Email to notes": manage which senders are allowed to create
 * notes by forwarding to the agent mailbox, verify the connection, trigger a
 * manual check, and see recent activity. Session-cookie authenticated.
 */
export function EmailIngest() {
  const [state, setState] = useState<State>('loading');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/email/status', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('load failed');
      setState((await res.json()) as EmailStatus);
    } catch {
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const value = address.trim();
    if (!value) return;
    setBusy(true);
    setNotice('');
    try {
      const res = await fetch('/api/email/allowlist', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: value }),
      });
      const body = (await res.json()) as { error?: string };
      if (!res.ok) setNotice(body.error ?? 'Could not add.');
      else setAddress('');
      await load();
    } catch {
      setNotice('Network error.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pattern: string) => {
    try {
      await fetch(`/api/email/allowlist/${encodeURIComponent(pattern)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
    } catch {
      /* reload reflects server state */
    }
    await load();
  };

  const verify = async () => {
    setBusy(true);
    setNotice('');
    try {
      const res = await fetch('/api/email/verify', { method: 'POST', credentials: 'same-origin' });
      const body = (await res.json()) as {
        ok?: boolean;
        mailboxId?: string;
        error?: string;
        mailboxes?: { mailbox: string; ok: boolean; mailboxId?: string; error?: string }[];
      };
      if (body.mailboxes?.length) {
        setNotice(
          body.mailboxes
            .map((m) => (m.ok ? `${m.mailbox}: connected (${m.mailboxId})` : `${m.mailbox}: failed — ${m.error}`))
            .join(' · '),
        );
      } else setNotice(body.ok ? `Connected — mailbox ${body.mailboxId}` : `Failed: ${body.error}`);
    } catch {
      setNotice('Network error.');
    } finally {
      setBusy(false);
    }
  };

  const checkNow = async () => {
    setBusy(true);
    setNotice('');
    try {
      const res = await fetch('/api/email/check', { method: 'POST', credentials: 'same-origin' });
      const body = (await res.json()) as {
        result?: {
          noted: number;
          skipped: number;
          errors: number;
          scanned: number;
          mailboxes?: { mailbox: string; scanned: number; noted: number; stoppedEarly?: string }[];
        };
        error?: string;
      };
      if (body.result) {
        const r = body.result;
        const per =
          r.mailboxes && r.mailboxes.length > 1
            ? ` (${r.mailboxes.map((m) => `${m.mailbox}: ${m.scanned}/${m.noted}${m.stoppedEarly ? ` ${m.stoppedEarly}` : ''}`).join(', ')})`
            : '';
        setNotice(`Scanned ${r.scanned} · noted ${r.noted} · skipped ${r.skipped} · errors ${r.errors}${per}`);
      } else setNotice(body.error ?? 'Check failed.');
      await load();
    } catch {
      setNotice('Network error.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="settings-section">
      <h3>Email to notes</h3>
      <p className="sd-hint">
        Forward or CC email to{' '}
        {state !== 'loading' && state !== 'error' && state.mailboxes.length > 1 ? (
          state.mailboxes.map((m, i) => (
            <span key={m.address}>
              {i > 0 ? ' or ' : ''}
              <code>{m.address}</code>
            </span>
          ))
        ) : (
          <code>{state !== 'loading' && state !== 'error' ? state.mailbox : '…'}</code>
        )}{' '}
        and allow-listed senders become notes automatically. Receipts and invoices are left
        untouched. The mailbox is never modified.
      </p>

      {state === 'loading' && <div className="sd-hint">Loading…</div>}
      {state === 'error' && (
        <div className="sd-api-error-row">
          <span className="sd-error">Couldn’t load status.</span>
          <button type="button" className="sd-btn" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {state !== 'loading' && state !== 'error' && (
        <>
          <div className="sd-hint">
            {state.enabled ? 'Polling is ON' : 'Polling is OFF (set EMAIL_INGEST_ENABLED=1)'}
            {' · '}
            {state.backend === 'imap' ? 'reading over IMAP' : 'reading via Hostinger API'}
            {' · '}
            {state.configured ? 'credentials configured' : 'no credentials set'}
            {state.sendConfirmation && state.recipients.length > 0 && (
              <>
                {' · '}confirmations via {state.provider === 'smtp' ? 'SMTP' : 'Hostinger'} →{' '}
                {state.recipients.join(', ')}
              </>
            )}
          </div>

          {state.mailboxes.length > 0 && (
            <ul className="sd-token-list">
              {state.mailboxes.map((m) => (
                <li key={m.address} className="sd-token">
                  <div className="sd-token-main">
                    <span className="sd-token-label">{m.address}</span>
                    <span className="sd-token-meta">
                      {m.configured ? `noted ${m.noted} · skipped ${m.skipped} · errors ${m.errors}` : m.hint ?? 'not configured'}
                      {m.configured && m.watermark > 0 ? ` · scanned to uid ${m.watermark}` : ''}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="sd-btn-row">
            <button type="button" className="sd-btn" disabled={busy} onClick={() => void verify()}>
              Verify connection
            </button>
            <button type="button" className="sd-btn" disabled={busy} onClick={() => void checkNow()}>
              Check now
            </button>
          </div>
          {notice && <div className="sd-hint">{notice}</div>}

          <div className="settings-field">
            <label htmlFor="sd-email-allow">Allowed senders</label>
            {state.trustHeader && (
              <div className="sd-hint">
                Not used right now: Exchange's trusted-senders list decides. Only mail Exchange marked{' '}
                {state.trustHeader}, or sent from inside the organization, becomes a note. This list
                applies again if that setting is removed.
              </div>
            )}
            {state.allowlist.length === 0 ? (
              <div className="sd-hint">None yet — add an address or a whole domain (@example.com).</div>
            ) : (
              <ul className="sd-token-list">
                {state.allowlist.map((p) => (
                  <li key={p} className="sd-token">
                    <div className="sd-token-main">
                      <span className="sd-token-label">{p}</span>
                    </div>
                    <button
                      type="button"
                      className="sd-btn danger sd-token-revoke"
                      onClick={() => void remove(p)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <form className="sd-token-form" onSubmit={(e) => void add(e)}>
            <div className="settings-field">
              <input
                id="sd-email-allow"
                value={address}
                placeholder="name@example.com or @example.com"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <button type="submit" className="sd-btn primary" disabled={busy || !address.trim()}>
              Add sender
            </button>
          </form>

          {state.recent.length > 0 && (
            <div className="settings-field">
              <label>Recent activity</label>
              <ul className="sd-email-recent">
                {state.recent.slice(0, 10).map((r) => (
                  <li key={r.messageId} className={`sd-email-row status-${r.status}`}>
                    <span className={`sd-scope-badge${r.status === 'noted' ? '' : ' read'}`}>
                      {r.status}
                    </span>
                    <span className="sd-email-subj">{r.subject || r.notePath || r.fromAddr || '—'}</span>
                    <span className="sd-token-meta">{dayjs(r.processedAt).fromNow()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
